/**
 * @fileoverview Assign Prospect Owner mutation hook.
 * @description Assigns the owner with the prospect's ETag as If-Match
 * (POST /sales/prospects/{prospectId}/assign-owner) and refreshes every sales query once it
 * succeeds. The ETag is the one the dialog loaded and showed, so a prospect that changed since is
 * refused (`412` / `409`) and shown for review rather than acted on blindly (3.5.11.0). The
 * command's Idempotency-Key outlives the dialog (see useIdempotentMutation), so a retry after an
 * unknown outcome replays it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useIdempotentMutation } from '@/features/sales/hooks/useIdempotentMutation'
import { assignProspectOwner } from '@/features/sales/services/prospects.service'

import type { ProspectOwnerAssignment } from '@/features/sales/types/prospect.types'

/** The input of one assign-owner command. */
export interface AssignProspectOwnerInput {
  prospectId: string
  etag: string
  ownerUserId: string
}

/**
 * @description Assigns or reassigns a prospect's owner.
 * @param {object} [options] - What follows a success.
 * @param {(result: ProspectOwnerAssignment, input: AssignProspectOwnerInput) => void}
 * [options.onSuccess] - Called once the owner is saved, even when the dialog has unmounted since.
 * @returns {ReturnType<typeof useIdempotentMutation>} The mutation (mutate with the prospect, the
 * ETag the dialog loaded and showed, and the owner).
 */
export function useAssignProspectOwner({
  onSuccess,
}: {
  onSuccess?:
    ((result: ProspectOwnerAssignment, input: AssignProspectOwnerInput) => void) | undefined
} = {}) {
  return useIdempotentMutation<AssignProspectOwnerInput, ProspectOwnerAssignment>({
    action: 'assign-owner',
    /**
     * @description Scopes the key to the prospect and the owner chosen, not the version it is sent against.
     * @param {AssignProspectOwnerInput} input - The assignment's input.
     * @returns {object} The prospect id and the chosen owner.
     */
    scopeOf: ({ prospectId, ownerUserId }) => ({ prospectId, ownerUserId }),
    /**
     * @description Sends the assignment under the key it is given.
     * @param {AssignProspectOwnerInput} input - The prospect, its version and the chosen owner.
     * @param {string} key - The Idempotency-Key.
     * @returns {Promise<ProspectOwnerAssignment>} The assignment.
     */
    send: ({ prospectId, etag, ownerUserId }, key) =>
      assignProspectOwner(prospectId, etag, ownerUserId, key),
    onSuccess,
  })
}
